/** @jsxImportSource ./simboluri-jsx */
import CoborareaIncarcarilor from "./CoborareaIncarcarilor";
import { useT } from "../i18n";
import { FemModel, IncarcariResult, StepState, fmtDate } from "./tipuri";
import ModeleRigiditate from "./ModeleRigiditate";
import AvertismenteMetoda from "./AvertismenteMetoda";
import Info from "./Info";
import LegendaCazuri from "./LegendaCazuri";
import { DescriereaPasului } from "./TitluPas";

// Pasul 8 — Încărcările gravitaționale: catalogul cazurilor (G1..S), aplicate
// pe modelul FEM al Pasului 7 și verificate prin echilibru global (suma
// reacțiunilor = totalul aplicat, per caz). Pereții nestructurali sunt mapați
// pe grinzi (linear) sau pe placă (uniform), per nivel.

export default function StepIncarcari({
  state,
  running,
  femState,
}: {
  state: StepState | null;
  running: boolean;
  // modelul Pasului 7 — vizualizarea 3D desenează pe geometria REALĂ
  femState?: StepState | null;
}) {
  const t = useT();
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.pas.ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.inc.ruleaza")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.inc.nerulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="incarcari" />
      </div>
    );
  }

  const r = state.result as unknown as IncarcariResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.pas.esuat", {
            eroare: r?.error ?? t("struct.pas.eroare_necunoscuta"),
          })}
        </div>
        <div className="sg-muted">{t("struct.pas.ruleaza_din_nou")}</div>
      </div>
    );
  }

  return (
    <div className="st-result">
      <ModeleRigiditate pas={8} r={r} />
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.inc.greutate")}
            <Info
              titlu={t("struct.inc.info_g_titlu")}
              ce={t("struct.inc.info_g_ce")}
              deCe={t("struct.inc.info_g_de_ce")}
              parametri={[
                { simbol: "G1", nume: t("struct.inc.p_g1"), sursa: t("struct.inc.p_g1_sursa") },
                { simbol: "G2a/G2b", nume: t("struct.inc.p_g2"), sursa: t("struct.inc.p_g2_sursa") },
                { simbol: "G3", nume: t("struct.inc.p_g3"), sursa: t("struct.inc.p_g3_sursa") },
              ]}
            />
          </div>
          <div className="st-card-value">{r.greutate_permanenta_kn} kN</div>
          <div className="st-card-sub">
            {t("struct.inc.cazuri_sub", {
              cazuri: r.nr_cazuri, elemente: r.nr_elemente,
            })}
          </div>
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.inc.echilibru")}
            <Info
              titlu={t("struct.inc.info_e_titlu")}
              ce={t("struct.inc.info_e_ce")}
              deCe={t("struct.inc.info_e_de_ce")}
            />
          </div>
          <div className="st-card-value" style={{ color: r.echilibru_ok ? "#4f8207" : "#b5323d" }}>
            {t(r.echilibru_ok ? "struct.inc.echilibru_ok" : "struct.inc.echilibru_nu")}
          </div>
          <div className="st-card-sub">
            {t(r.echilibru_ok
              ? "struct.inc.echilibru_ok_sub" : "struct.inc.echilibru_nu_sub")}
          </div>
        </div>
      </div>

      {r.date_intrare?.length ? (
        <div className="st-findings">
          <h3>
            {t("struct.inc.date_intrare")}
            <Info
              titlu={t("struct.inc.info_d_titlu")}
              ce={t("struct.inc.info_d_ce")}
              deCe={t("struct.inc.info_d_de_ce")}
            />
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table className="sg-table st-strat">
              <thead>
                <tr>
                  <th>{t("struct.inc.col_marime")}</th>
                  <th>{t("struct.inc.col_simbol")}</th>
                  <th>{t("struct.inc.col_valoare")}</th>
                  <th>{t("struct.inc.col_de_unde")}</th>
                  <th>{t("struct.inc.col_articol")}</th>
                </tr>
              </thead>
              <tbody>
                {r.date_intrare.map((d, i) => (
                  <tr key={`${d.simbol}-${i}`}>
                    <td>{d.marime}</td>
                    <td className="sg-mono">{d.simbol}</td>
                    <td className="sg-mono" style={{ whiteSpace: "nowrap" }}>
                      {d.valoare} {d.unitate !== "—" ? d.unitate : ""}
                    </td>
                    <td className="sg-muted">{d.de_unde}</td>
                    <td className="sg-muted">{d.articol}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {r.niveluri?.length ? (
        <CoborareaIncarcarilor
          niveluri={r.niveluri}
          unitare={r.valori_unitare ?? []}
          infra={r.infrastructura ?? null}
          mase={r.mase_detaliu ?? []}
          model={((femState?.result as { model?: FemModel } | null)
            ?.model) ?? null}
          aplicare={r.aplicare ?? []}
        />
      ) : null}

      <LegendaCazuri
        legenda={r.legenda_cazuri ?? []}
        unde
        notaCaz={(l) => {
          // panourile de subsol nu mai sunt bare, deci greutatea lor nu apare
          // ca linie în harta 3D: se aplică nodal pe fâșiile de shell. Fără
          // cifra asta părea că lipsește din G1.
          const pw = (r.aplicare ?? []).find((a) => a.caz === l.caz)
            ?.pereti_subsol_kn;
          return pw ? (
            <div className="sg-muted" style={{ fontSize: 11, whiteSpace: "normal" }}>
              {t("struct.inc.nota_panouri", { n: pw })}
            </div>
          ) : null;
        }}
      />

      <div className="st-findings">
        <h3>{t("struct.inc.echilibru_titlu")}</h3>
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.inc.col_caz")}</th>
              <th>{t("struct.inc.col_total")}</th>
              <th>{t("struct.inc.col_reactiuni")}</th>
              <th>{t("struct.inc.col_dezechilibru")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {r.verificare_echilibru.map((v) => (
              <tr key={v.caz} style={v.ok ? {} : { fontWeight: 600 }}>
                <td className="sg-mono"><strong>{v.caz}</strong></td>
                <td className="sg-mono">{v.total_aplicat_kn} kN</td>
                <td className="sg-mono">{v.suma_reactiuni_kn} kN</td>
                <td className="sg-mono">{v.dezechilibru_pct}%</td>
                <td>
                  {v.ok ? (
                    <span className="st-ok">✓</span>
                  ) : (
                    <span className="st-bad">✗</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={r.echilibru_ok ? "fem-ok" : "st-warn"}>
          {t(r.echilibru_ok
            ? "struct.inc.echilibru_final_ok"
            : "struct.inc.echilibru_final_nu")}
        </div>
      </div>

      {r.pereti && r.pereti.length > 0 && (
        <div className="st-findings">
          <h3>
            {t("struct.inc.pereti")}
            <Info
              titlu={t("struct.inc.info_p_titlu")}
              ce={t("struct.inc.info_p_ce")}
              deCe={t("struct.inc.info_p_de_ce")}
              parametri={[
                { simbol: "q_perete", nume: t("struct.inc.p_q"), sursa: t("struct.inc.p_q_sursa") },
                { simbol: t("struct.inc.p_aria"), nume: t("struct.inc.p_aria_nume"), sursa: t("struct.inc.p_aria_sursa") },
              ]}
            />
          </h3>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.top.nivel")}</th>
                <th>{t("struct.inc.col_pe_grinzi")}</th>
                <th>{t("struct.inc.col_pe_placa")}</th>
                <th>{t("struct.inc.col_q")}</th>
                <th>{t("struct.inc.col_arie_c")}</th>
              </tr>
            </thead>
            <tbody>
              {r.pereti.map((p, i) => (
                <tr key={i}>
                  <td><strong>{p.nivel}</strong></td>
                  <td className="sg-mono">{p.pe_grinzi_m} m</td>
                  <td className="sg-mono">{p.pe_placa_m} m</td>
                  <td className="sg-mono">{p.q_perete_kn_ml} kN/ml</td>
                  <td className="sg-mono">{p.arie_c_m2} m²</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {r.metoda?.length ? (
        <AvertismenteMetoda
          avertismente={r.metoda}
          titlu="struct.am.incarcari_titlu"
          numeAtentie="struct.am.incarcari_atentie"
          intro="struct.am.incarcari_intro"
        />
      ) : null}

      <div className="sg-muted">{r.nota}</div>
      <div className="sg-muted st-ran-at">
        {t("struct.pas.rulat_la", { cand: fmtDate(state.updated_at) })}
      </div>
    </div>
  );
}
